import {
  isServerStartGuideStatus,
  START_GUIDE_FRESH_VISIT,
} from "./startGuideStatusSource";

export const START_GUIDE_CLIENT_STATUS_KEY = "lappa.startGuide.clientStatus.v1";

const EMPTY = { projects: {}, server: {} };

let snapshot = readStorage();
const listeners = new Set();

function readStorage() {
  if (typeof window === "undefined") {
    return { projects: {}, server: {} };
  }

  if (START_GUIDE_FRESH_VISIT) {
    window.localStorage.removeItem(START_GUIDE_CLIENT_STATUS_KEY);
    return { projects: {}, server: {} };
  }

  try {
    const raw = window.localStorage.getItem(START_GUIDE_CLIENT_STATUS_KEY);
    if (!raw) return { projects: {}, server: {} };
    const parsed = JSON.parse(raw);
    return {
      projects: parsed?.projects && typeof parsed.projects === "object" ? parsed.projects : {},
      // Step ticks come from GET /user/start_guide and are not restored from disk.
      server: {},
    };
  } catch {
    return { projects: {}, server: {} };
  }
}

function emit(next) {
  snapshot = next;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      START_GUIDE_CLIENT_STATUS_KEY,
      JSON.stringify({ ...next, server: {} })
    );
  }
  listeners.forEach((listener) => listener());
}

export function subscribeStartGuideStatus(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getStartGuideStatusSnapshot() {
  return snapshot;
}

export function getProjectStartGuideStatus(project) {
  return snapshot.projects[project] || { steps: {}, dismissed: false, collapsed: false, notice: null };
}

export function getServerStartGuideStatus(project) {
  return snapshot.server[project] || null;
}

function updateProject(project, updater) {
  const current = getProjectStartGuideStatus(project);
  const nextProject = updater(current);
  emit({
    ...snapshot,
    projects: { ...snapshot.projects, [project]: nextProject },
  });
}

const freshSaves = new Set();

export function holdStartGuideStep(project, stepId) {
  if (!project || !stepId) return;
  freshSaves.add(`${project}:${stepId}`);
}

export function hasStartGuideHold(project, stepId) {
  return freshSaves.has(`${project}:${stepId}`);
}

export function releaseStartGuideHold(project, stepId) {
  freshSaves.delete(`${project}:${stepId}`);
}

export function recordClientStep(project, stepId, complete = true, extra = {}) {
  if (!project || !stepId || isServerStartGuideStatus()) return;
  updateProject(project, (current) => ({
    ...current,
    steps: {
      ...current.steps,
      [stepId]: {
        complete: Boolean(complete),
        skipped: Boolean(extra.skipped),
        visited:
          extra.visited != null
            ? Boolean(extra.visited)
            : Boolean(current.steps?.[stepId]?.visited),
        updatedAt: Date.now(),
      },
    },
  }));
}

export function setStartGuideDismissed(project, dismissed) {
  updateProject(project, (current) => ({ ...current, dismissed: Boolean(dismissed) }));
}

export function setStartGuideCollapsed(project, collapsed) {
  updateProject(project, (current) => ({ ...current, collapsed: Boolean(collapsed) }));
}

export function setStartGuideNotice(project, notice) {
  updateProject(project, (current) => ({ ...current, notice: notice || null }));
}

export function setStartGuideCompletionNoticeDismissed(project, dismissed) {
  updateProject(project, (current) => ({
    ...current,
    completionNoticeDismissed: Boolean(dismissed),
  }));
}

/**
 * Drop-in for the future API response. Ignored while START_GUIDE_STATUS_SOURCE
 * is "client", and used as soon as that flag is "server".
 */
export function setServerStartGuideStatus(project, payload) {
  emit({
    ...snapshot,
    server: { ...snapshot.server, [project]: payload || null },
  });
}

export function resetStartGuideHoldsForTests() {
  freshSaves.clear();
}

export function resetStartGuideStatusForTests() {
  freshSaves.clear();
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(START_GUIDE_CLIENT_STATUS_KEY);
  }
  emit({ projects: {}, server: {} });
}

export { EMPTY as EMPTY_START_GUIDE_STATUS };
